// PTZ 純邏輯（APP#11，cluster 0x8060 — spec 21 §3.8）。
// 方向→相對角度 delta 對照、PTZ 事件分類（移動中/追蹤中/停止）、Zoom 型別自適應、
// calibrate 狀態機判定皆為純函式，與 DOM/signaling 解耦以利 vitest。
//
// 64-bit id 逐字取自 spec 21 §3.8 / common/04 §4.14（硬規則：不自創）。

// ---- ids（spec 21 §3.8 表，逐字照抄）----
export const PTZ_OBJECT_TRACKING = '0x2100008060000000'; // ATTR SET bool
export const PTZ_ZOOM = '0x2100008060000100'; // ATTR SET enum/int（型別未收斂 GAP-DS-6）
export const PTZ_MOVE_RELATIVE = '0x3100008060000000'; // CMD strVal "pan,tilt"（相對角度）
export const PTZ_MOVE_ABSOLUTE = '0x3100008060000100'; // CMD strVal（絕對角度；非 preset 儲存路徑）
export const PTZ_STOP = '0x3100008060000200'; // CMD 無參數
export const PTZ_CALIBRATE = '0x3100008060000300'; // CMD + 單次確認
export const PTZ_SET_PRESET = '0x3100008060000400'; // CMD intVal preset_no；device 保存目前 PTZ pose
export const PTZ_GOTO_PRESET = '0x3100008060000500'; // CMD intVal preset_no；移動到 device-side preset
export const PTZ_CLEAR_PRESET = '0x3100008060000600'; // CMD intVal preset_no；清除 device-side preset
export const PTZ_LIST_PRESETS = '0x3100008060000700'; // CMD 無參數；列出 device-side preset slots

// EVENT ids（spec 21 §3.8：Move/Track 事件 room 廣播）：
//   0x1100008060000000 Move 開始 / 0x1100008060000100 MoveStop
//   0x1100008060000200 Track 開始 / 0x1100008060000300 Track 停止
export const PTZ_EVT_MOVE_START = '0x1100008060000000';
export const PTZ_EVT_MOVE_STOP = '0x1100008060000100';
export const PTZ_EVT_TRACK_START = '0x1100008060000200';
export const PTZ_EVT_TRACK_STOP = '0x1100008060000300';

// ---- 方向盤（8 向）----
export type PtzDirection = 'up' | 'down' | 'left' | 'right' | 'up-left' | 'up-right' | 'down-left' | 'down-right';

export const PTZ_DIRECTIONS: PtzDirection[] = ['up-left', 'up', 'up-right', 'left', 'right', 'down-left', 'down', 'down-right'];

/** 每次點按的相對角度增量（合理常數；對角線同步 pan/tilt）。 */
export const PTZ_STEP_DEG = 10;

/**
 * 方向 → MoveRelative 的 `"pan,tilt"` strVal（相對角度）。
 * 慣例：pan 右為正、tilt 上為正（spec 21 §3.8 範例 "15,-10"=右下）。
 */
export function directionToPanTilt(dir: PtzDirection, step: number = PTZ_STEP_DEG): string {
  const pan = dir.includes('right') ? step : dir.includes('left') ? -step : 0;
  const tilt = dir.includes('up') ? step : dir.includes('down') ? -step : 0;
  return `${pan},${tilt}`;
}

// ---- PTZ room 事件分類（驅動 badge）----
export type PtzActivity = 'moving' | 'tracking' | 'idle';

/** 由 event_id 判定 PTZ 活動狀態變化；非 PTZ 事件回 null（呼叫端忽略）。 */
export function ptzActivityOf(eventId: string | undefined): PtzActivity | null {
  const id = eventId?.toLowerCase();
  switch (id) {
    case PTZ_EVT_MOVE_START:
      return 'moving';
    case PTZ_EVT_TRACK_START:
      return 'tracking';
    case PTZ_EVT_MOVE_STOP:
    case PTZ_EVT_TRACK_STOP:
      return 'idle';
    default:
      return null;
  }
}

/** calibrate 進行中收到 MoveStop（0x...0100）即解除（spec 21 §3.8）。 */
export function isCalibrateClearEvent(eventId: string | undefined): boolean {
  return eventId?.toLowerCase() === PTZ_EVT_MOVE_STOP;
}

// ---- Zoom 型別自適應（GAP-DS-6）----
export type ZoomKind = 'int' | 'enum' | 'unknown';

/**
 * 依 report 實際值推斷 Zoom widget 型別：
 *   - number → int（slider/stepper）
 *   - string → enum（segmented；如裝置回 "low"/"high"）
 *   - 其他/未知 → unknown（widget 隱藏，等待 report）
 * GAP-DS-6 收斂前以此自適應。
 */
export function zoomKind(value: unknown): ZoomKind {
  if (typeof value === 'number') return 'int';
  if (typeof value === 'string') return 'enum';
  return 'unknown';
}

/** int Zoom 的合理倍率邊界（GAP-DS-6 未定版前的過渡值，建議 1–N 倍率）。 */
export const ZOOM_INT_MIN = 1;
export const ZOOM_INT_MAX = 10;

/** 下一個 int zoom 值（夾在 [min,max]）。 */
export function nextZoomInt(current: number, delta: number, min = ZOOM_INT_MIN, max = ZOOM_INT_MAX): number {
  const v = Math.round(current + delta);
  if (Number.isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}

/** capability_effective 是否含 PTZ cluster（0x8060，大小寫無關）。 */
export function hasPtzCluster(clusters: Iterable<string> | undefined): boolean {
  if (!clusters) return false;
  for (const c of clusters) {
    if (c?.toLowerCase() === '0x8060') return true;
  }
  return false;
}

// ---- PTZ 預置點（APP#47，cluster 0x8060 SetPreset/GotoPreset/ClearPreset/ListPresets）----
// device 端以 preset slot（1..255）保存 PTZ pose；SetPreset 存目前位置、GotoPreset 移動過去、
// ClearPreset 清除。dictionary **無 preset name attr** → 名稱僅存 client-local（per device_id）。
// 純邏輯（slot 配置 / upsert / 移除 / 解析）在此,localStorage I/O 在 PtzSection。
export interface PtzPreset {
  slot: number;
  name: string;
}

export const PTZ_PRESET_MIN = 1;
export const PTZ_PRESET_MAX = 255;

/** localStorage key（per device_id 隔離）。 */
export function presetStorageKey(deviceId: string): string {
  return `ptz_presets__${deviceId}`;
}

/** 下一個可用 slot（min..max）；已滿回 null。 */
export function nextFreePresetSlot(presets: PtzPreset[], min = PTZ_PRESET_MIN, max = PTZ_PRESET_MAX): number | null {
  const used = new Set(presets.map((p) => p.slot));
  for (let s = min; s <= max; s++) {
    if (!used.has(s)) return s;
  }
  return null;
}

/** 加入/覆蓋一個 preset（依 slot），回新陣列（依 slot 升序）。 */
export function upsertPreset(presets: PtzPreset[], slot: number, name: string): PtzPreset[] {
  const rest = presets.filter((p) => p.slot !== slot);
  return [...rest, { slot, name }].sort((a, b) => a.slot - b.slot);
}

/** 移除一個 slot，回新陣列。 */
export function removePreset(presets: PtzPreset[], slot: number): PtzPreset[] {
  return presets.filter((p) => p.slot !== slot);
}

/** 解析 localStorage 原字串為 preset 陣列（防呆：非法/越界一律濾除，依 slot 升序）。 */
export function parsePresets(raw: string | null): PtzPreset[] {
  if (!raw) return [];
  try {
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return (arr as unknown[])
      .filter((p): p is PtzPreset => {
        if (!p || typeof p !== 'object') return false;
        const o = p as Record<string, unknown>;
        return typeof o.slot === 'number' && typeof o.name === 'string' && o.slot >= PTZ_PRESET_MIN && o.slot <= PTZ_PRESET_MAX;
      })
      .sort((a, b) => a.slot - b.slot);
  } catch {
    return [];
  }
}
